import { Chip, Box } from "@mui/material";
import CutterTooltip, { CutterTooltipSeverity } from '../common/CutterTooltip';
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import * as React from "react";
import { NavigateFunction } from 'react-router-dom';
import { EquipmentUpgradePriorityData } from "../stats/types/stats";
import { navigateTabsWithQueries } from "../types/cutterState";

// Define DataGrid columns function that accepts navigate function
export const getEquipmentUpgradeColumns = (navigate: NavigateFunction): GridColDef[] => [
    {
        field: 'RegionalRank',
        headerName: 'Rank',
        width: 60,
        type: 'number',
        renderCell: (params: GridRenderCellParams<EquipmentUpgradePriorityData, number>) => (
            <Chip
                label={params.value}
                color={params.value && params.value <= 10 ? 'secondary' :
                    params.value && params.value <= 25 ? 'warning' : 'default'}
                size="small"
                variant={params.value && params.value <= 10 ? 'filled' : 'outlined'}
            />
        ),
        sortable: true
    },
    {
        field: 'UpgradeNeededIndex',
        headerName: 'Need',
        width: 80,
        type: 'number',
        valueFormatter: (value: Number) =>
            value ? `${value.toFixed(2)}%` : '',
        renderCell: (params: GridRenderCellParams<EquipmentUpgradePriorityData, number>) => (
            <Box sx={{
                color: params.value && params.value > 95 ? 'error.main' :
                    params.value && params.value > 90 ? 'warning.main' : 'text.primary',
                fontWeight: params.value && params.value > 95 ? 'bold' : 'normal'
            }}>
                {params.value ? `${params.value.toFixed(2)}%` : ''}
            </Box>
        ),
        sortable: true
    },
    {
        field: 'SerialNumber',
        headerName: 'SN',
        width: 80,
        sortable: true,
        renderCell: (params: GridRenderCellParams<EquipmentUpgradePriorityData, string>) => (
            <Box
                onClick={() => navigateTabsWithQueries(navigate, 'bookmarks', params.value ?? '')}
                sx={{
                    color: 'inherit',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    '&:hover': {
                        color: 'primary.main'
                    }
                }}
            >
                {params.value}
            </Box>
        )
    },
    {
        field: 'Company_Name',
        headerName: 'Customer',
        width: 280,
        sortable: true
    },
    {
        field: 'PlantName',
        headerName: 'Plant',
        width: 180,
        sortable: true
    },
    {
        field: 'Country',
        headerName: 'Country',
        width: 120,
        sortable: true
    },
    {
        field: 'Region',
        headerName: 'Region',
        width: 120,
        sortable: true
    },
    {
        field: 'CompanyContractRate',
        headerName: 'Comp. Contracts',
        width: 120,
        type: 'number',
        valueFormatter: (value: number) => (value ? `${(100 * value).toFixed(1)}%` : ''),
        sortable: true
    },
    {
        field: 'STATS_SoftwareUpdateIndex',
        headerName: 'Software Index',
        width: 100,
        type: 'number',
        valueFormatter: (value: number) =>
            value ? `${value.toFixed(2)}` : '0.00',
        renderCell: (params: GridRenderCellParams<EquipmentUpgradePriorityData, number>) => {
            const severity: CutterTooltipSeverity = params.value === 0 ? 'error' : (params.value && params.value < 10) ? 'warning' : 'success';
            return (
            <CutterTooltip severity={severity}
                title="Software Update Index - Higher values indicate recent updates"
                copyText={`Software Index: ${params.value ? params.value.toFixed(2) : '0.00'}`}>
                <Box sx={{
                    color: params.value === 0 ? 'error.main' :
                        params.value && params.value < 10 ? 'warning.main' : 'success.main'
                }}>
                    {params.value ? params.value.toFixed(2) : '0.00'}
                </Box>
            </CutterTooltip>
        );},
        sortable: true
    },
    {
        field: 'STATS_ConnectionRate',
        headerName: 'Connection',
        width: 130,
        type: 'number',
        valueFormatter: (value: number) => value ? `${value.toFixed(2)}%` : '0.00%',
        renderCell: (params: GridRenderCellParams<EquipmentUpgradePriorityData, number>) => {
            const severity: CutterTooltipSeverity = params.value === 0 ? 'error' : (params.value && params.value < 15) ? 'warning' : 'success';
            return (
            <CutterTooltip severity={severity}
                title="Connection Rate - Percentage of time connected to services"
                copyText={`Connection Rate: ${params.value ? params.value.toFixed(2) : '0.00'}%`}>
                <Box sx={{
                    color: params.value === 0 ? 'error.main' :
                        params.value && params.value < 15 ? 'warning.main' : 'success.main'
                }}>
                    {params.value ? `${params.value.toFixed(2)}%` : '0.00%'}
                </Box>
            </CutterTooltip>
        );},
        sortable: true
    },
    {
        field: 'HARD_CUT_SoldDate',
        headerName: 'Delivery',
        width: 120,
        type: 'date',
        valueFormatter: (value: Date) => {
            if (!value) return '';
            const date = new Date(value);
            return date.toLocaleDateString();
        },
        sortable: true
    },
    {
        field: 'HARD_Machine_type',
        headerName: 'Machine Type',
        width: 180,
        sortable: true
    }
];